import { useState } from 'react';

import type { App } from '@keboola/api-client/developerPortal/types';
import { Alert, Button, Modal, setSubmitError, toast, useAppForm } from '@keboola/design';

import { getApiErrorMessage } from '../../auth/errors';
import { formValidators } from '../../auth/validate';
import { useDeprecateApp } from '../queries';
import { deprecateAppFormSchema } from '../schemas';

const FORM_ID = 'deprecate-app';

/**
 * Deprecates one of the vendor's own components.
 *
 * - A `Modal`, not an `AlertModal`: `AlertModal` closes via `.finally()`, even on a rejected deprecation.
 * - The date is optional, as in apps-api.
 */
export function DeprecateAppModal({ app, vendorId }: { app: App; vendorId: string }) {
  const [open, setOpen] = useState(false);
  const deprecateApp = useDeprecateApp(vendorId);

  const form = useAppForm({
    defaultValues: { expiredOn: '' },
    validators: formValidators(deprecateAppFormSchema),
    canSubmitWhenInvalid: true,
    onSubmit: async ({ value, formApi }) => {
      try {
        await deprecateApp.mutateAsync({
          appId: app.id,
          expiredOn: value.expiredOn === '' ? undefined : value.expiredOn,
        });
        toast.success(`${app.name} has been deprecated.`);
        setOpen(false);
        form.reset();
      } catch (error) {
        setSubmitError(formApi, getApiErrorMessage(error, `Could not deprecate ${app.name}.`));
      }
    },
  });

  return (
    <Modal
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        // Reset on close: no stale half-typed date on a one-way action.
        if (!next) form.reset();
      }}
    >
      <Modal.Trigger asChild>
        <Button variant="secondary" size="small">
          Deprecate
        </Button>
      </Modal.Trigger>
      <Modal.Content>
        <Modal.Header description={app.id}>Deprecate {app.name}</Modal.Header>
        <Modal.Body>
          <div className="tw:flex tw:flex-col tw:gap-4">
            <Alert variant="warning" title="You cannot undo this yourself">
              <p className="tw:m-0">
                The component stops being offered for new configurations and is removed from the
                public catalog. Existing configurations keep running, and show a deprecation
                warning.
              </p>
              <p className="tw:m-0">
                Only Keboola can bring a deprecated component back — contact{' '}
                {/* Underlined, not colour-only (WCAG 1.4.1). */}
                <a className="tw:text-primary tw:underline" href="mailto:support@keboola.com">
                  support@keboola.com
                </a>{' '}
                if you deprecate this by mistake.
              </p>
            </Alert>

            <form.AppForm>
              <form.FormBody id={FORM_ID}>
                <form.AppField name="expiredOn">
                  {(field) => (
                    <field.TextField
                      label="Shutdown date"
                      requiredMark="optional"
                      placeholder="YYYY-MM-DD"
                      help="Shown to anyone still using the component. Leave blank if there is no date yet."
                    />
                  )}
                </form.AppField>
                <form.SubmitErrorAlert />
              </form.FormBody>
            </form.AppForm>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Modal.Close asChild>
            <Button variant="secondary">Cancel</Button>
          </Modal.Close>
          <form.AppForm>
            <form.SubmitButton form={FORM_ID} variant="danger">
              Deprecate component
            </form.SubmitButton>
          </form.AppForm>
        </Modal.Footer>
      </Modal.Content>
    </Modal>
  );
}
